import { useTheme, Box } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

import { MessageThreadRole, TMessageSession, TMessageThreadMessage } from '@/types/message';

import Avatar from '@/@mantis/components/@extended/Avatar';
import { getPaletterColorFromString } from '@/utils/color';
import { formatFullDateTime } from '@/utils/date';
import { getInitialsFromFullName } from '@/utils/user';

import MessageThreadMessageContent from './MessageThreadMessageContent';

interface Props {
  message: TMessageThreadMessage;
  messageSession: TMessageSession;
}

const MessageThreadMessageItem: React.FC<Props> = ({ message, messageSession }) => {
  const theme = useTheme();

  const isExternalMessage = useMemo(
    () => [MessageThreadRole.tenant, MessageThreadRole.vendor].includes(message.role),
    [message.role]
  );

  // External messages (tenant/vendor) - shown on the left with avatar
  if (isExternalMessage) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ maxWidth: '70%' }}>
          <Avatar
            color={getPaletterColorFromString(messageSession.session_id)}
            type="filled"
            sx={{ width: 40, height: 40, fontSize: '0.875rem' }}
          >
            {getInitialsFromFullName(messageSession.from.name ?? 'Unknown')}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            {messageSession.subject && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                Re: {messageSession.subject}
              </Typography>
            )}
            <Card
              elevation={0}
              sx={{
                bgcolor: theme.palette.grey[100],
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <MessageThreadMessageContent
                  message={message}
                  isExternalMessage={isExternalMessage}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    mt: 1,
                    display: 'block',
                  }}
                >
                  {formatFullDateTime(new Date(message.updated_at))}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Box>
    );
  }

  // Internal messages (property_manager) - shown on the right
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
      <Box sx={{ maxWidth: '70%' }}>
        {messageSession.subject && (
          <Typography
            variant="caption"
            align="right"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.75rem',
              mb: 0.5,
              display: 'block',
            }}
          >
            Re: {messageSession.subject}
          </Typography>
        )}
        <Card
          elevation={0}
          sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: 2,
            ml: 'auto',
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <MessageThreadMessageContent message={message} isExternalMessage={isExternalMessage} />
            <Typography
              variant="caption"
              align="right"
              sx={{
                color: theme.palette.primary.contrastText,
                mt: 1,
                display: 'block',
                opacity: 0.8,
              }}
            >
              {formatFullDateTime(new Date(message.updated_at))}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default MessageThreadMessageItem;
